import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export type TableSortState = {
  columnId: string;
  direction: SortDirection;
} | null;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage may be unavailable */
  }
}

function removeKey(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function visibilityStorageKey(columnStorageKey: string) {
  return `data-table:${columnStorageKey}:visibility`;
}

export function sortStorageKey(columnStorageKey: string) {
  return `data-table:${columnStorageKey}:sort`;
}

export function loadHiddenColumnIds(
  columnStorageKey: string,
  validIds: Set<string>
): Set<string> {
  const stored = readJson<string[]>(visibilityStorageKey(columnStorageKey));
  if (!stored?.length) return new Set();
  return new Set(stored.filter((id) => validIds.has(id)));
}

export function saveHiddenColumnIds(columnStorageKey: string, hiddenIds: Set<string>) {
  writeJson(visibilityStorageKey(columnStorageKey), Array.from(hiddenIds));
}

export function loadSortState(
  columnStorageKey: string,
  validIds: Set<string>
): TableSortState {
  const stored = readJson<{ columnId?: string; direction?: SortDirection }>(
    sortStorageKey(columnStorageKey)
  );
  if (!stored?.columnId || !stored.direction || !validIds.has(stored.columnId)) {
    return null;
  }
  if (stored.direction !== 'asc' && stored.direction !== 'desc') return null;
  return { columnId: stored.columnId, direction: stored.direction };
}

export function saveSortState(columnStorageKey: string, sort: TableSortState) {
  if (!sort) {
    removeKey(sortStorageKey(columnStorageKey));
    return;
  }
  writeJson(sortStorageKey(columnStorageKey), sort);
}

export function useColumnVisibility(
  columnStorageKey: string | undefined,
  hideableColumnIds: string[],
  initialHidden?: Set<string>
) {
  const validIds = useMemo(() => new Set(hideableColumnIds), [hideableColumnIds]);

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => {
    if (columnStorageKey) {
      return loadHiddenColumnIds(columnStorageKey, validIds);
    }
    return initialHidden ?? new Set();
  });

  const setHidden = useCallback(
    (next: Set<string>) => {
      setHiddenIds(next);
      if (columnStorageKey) saveHiddenColumnIds(columnStorageKey, next);
    },
    [columnStorageKey]
  );

  const toggleColumn = useCallback(
    (id: string, visibleCount: number) => {
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          if (visibleCount <= 1) return prev;
          next.add(id);
        }
        if (columnStorageKey) saveHiddenColumnIds(columnStorageKey, next);
        return next;
      });
    },
    [columnStorageKey]
  );

  const resetVisibility = useCallback(() => {
    const next = new Set<string>();
    setHiddenIds(next);
    if (columnStorageKey) removeKey(visibilityStorageKey(columnStorageKey));
  }, [columnStorageKey]);

  return { hiddenIds, toggleColumn, resetVisibility, setHidden };
}

export function useTableSort(
  columnStorageKey: string | undefined,
  sortableColumnIds: string[],
  defaultSort?: TableSortState
) {
  const validIds = useMemo(() => new Set(sortableColumnIds), [sortableColumnIds]);

  const [sort, setSortState] = useState<TableSortState>(() => {
    if (columnStorageKey) {
      const stored = loadSortState(columnStorageKey, validIds);
      if (stored) return stored;
    }
    if (defaultSort && validIds.has(defaultSort.columnId)) return defaultSort;
    return null;
  });

  const setSort = useCallback(
    (next: TableSortState) => {
      setSortState(next);
      if (columnStorageKey) saveSortState(columnStorageKey, next);
    },
    [columnStorageKey]
  );

  const cycleSort = useCallback(
    (columnId: string) => {
      setSortState((prev) => {
        let next: TableSortState;
        if (!prev || prev.columnId !== columnId) {
          next = { columnId, direction: 'asc' };
        } else if (prev.direction === 'asc') {
          next = { columnId, direction: 'desc' };
        } else {
          next = null;
        }
        if (columnStorageKey) saveSortState(columnStorageKey, next);
        return next;
      });
    },
    [columnStorageKey]
  );

  const resetSort = useCallback(() => {
    setSortState(null);
    if (columnStorageKey) removeKey(sortStorageKey(columnStorageKey));
  }, [columnStorageKey]);

  return { sort, setSort, cycleSort, resetSort };
}

export function clearTablePreferences(columnStorageKey: string) {
  removeKey(`data-table:${columnStorageKey}`);
  removeKey(visibilityStorageKey(columnStorageKey));
  removeKey(sortStorageKey(columnStorageKey));
}
